'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { getProducts } from '@/lib/shopify'
import { useCart } from '@/lib/CartContext'
import type { TransformedProduct } from '@/lib/shopify.types'
import CartDrawer from './CartDrawer'

interface StockProduct extends TransformedProduct {
  change: number
  volume: number
}

export default function Navigation() {
  const pathname = usePathname()
  const isShopPage = pathname === '/shop'
  const [products, setProducts] = useState<StockProduct[]>([])
  const { itemCount, items, clearCart } = useCart()
  const [notification, setNotification] = useState<string | null>(null)
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(0.5)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  
  // Audio URL - can be updated via environment variable or direct edit
  // You can replace this with your Shopify CDN URL: https://cdn.shopify.com/s/files/1/your-store-id/files/your-audio-file.mp3
  const audioUrl = process.env.NEXT_PUBLIC_BACKGROUND_MUSIC_URL || "https://cdn.shopify.com/s/files/1/0675/1328/6842/files/background-music.mp3"
  
  // Toggle audio playback
  const toggleAudio = () => {
    if (!audioRef.current) return
    
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play().catch(err => {
        console.error('Error playing audio:', err)
        setNotification('Autoplay blocked. Click again to play.')
        setTimeout(() => setNotification(null), 2000)
      })
    }
    setIsPlaying(!isPlaying)
  }
  
  // Volume control function
  const adjustVolume = (direction: 'up' | 'down') => {
    let newVolume = direction === 'up' 
      ? Math.min(1, volume + 0.1) 
      : Math.max(0, volume - 0.1)
    
    setVolume(Number(newVolume.toFixed(1)))
    
    if (audioRef.current) {
      audioRef.current.volume = newVolume
    }
  }
  
  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio(audioUrl)
    audioRef.current.loop = true
    audioRef.current.volume = volume
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [audioUrl])
  
  // Update volume when changed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume
    }
  }, [volume])

  // Function to redirect to Shopify cart
  const goToShopifyCart = async () => {
    if (itemCount > 0) {
      try {
        // Map cart items to the format expected by the API
        const cartItems = items.map(item => ({
          id: item.variantId,
          quantity: item.quantity
        }));

        const response = await fetch('/api/cart/create', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: cartItems }),
        });

        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.error || 'Failed to create cart');
        }

        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          console.error('No checkout URL returned');
          setNotification('Error: Could not create cart');
          setTimeout(() => setNotification(null), 2000);
        }
      } catch (error) {
        console.error('Error creating cart:', error);
        setNotification('Error: Could not create cart');
        setTimeout(() => setNotification(null), 2000);
      }
    }
  }

  // Modify the products useEffect to run only on client-side
  useEffect(() => {
    let mounted = true;
    
    getProducts().then(shopifyProducts => {
      if (!mounted) return;
      const stockProducts = shopifyProducts.map(product => ({
        ...product,
        change: (Math.random() * 20) - 10,
        volume: Math.floor(Math.random() * 300) + 50
      }))
      setProducts(stockProducts)
    })
    
    return () => {
      mounted = false;
    }
  }, [])

  // Modify the price updates useEffect
  useEffect(() => {
    if (typeof window === 'undefined' || products.length === 0) return;

    const generateMarketMove = (basePrice: number) => {
      const maxPercentageChange = 1.0 // Maximum 1% change per tick
      const percentageChange = (Math.random() - 0.5) * 2 * maxPercentageChange
      const change = basePrice * (percentageChange / 100)
      return Math.max(basePrice * 0.5, basePrice + change)
    }

    const interval = setInterval(() => {
      setProducts(prevProducts => 
        prevProducts.map(product => {
          const currentPrice = typeof product.price === 'string' ? parseFloat(product.price) : product.price
          const newPrice = generateMarketMove(currentPrice)
          
          // Calculate percentage change from previous price
          const priceChange = ((newPrice - currentPrice) / currentPrice) * 100
          
          const volumeChange = Math.floor((Math.random() - 0.5) * 20)
          const newVolume = Math.max(0, product.volume + volumeChange)
          
          return {
            ...product,
            price: newPrice.toFixed(2),
            change: priceChange,
            volume: newVolume
          }
        })
      )
    }, 1000) // Update every second to match shop page

    return () => clearInterval(interval)
  }, [products.length])

  useEffect(() => {
    // Force a reflow to ensure the ticker is visible
    const ticker = document.querySelector('.status-ticker-content') as HTMLDivElement;
    if (ticker) {
      ticker.style.display = 'none';
      ticker.offsetHeight; // Force reflow
      ticker.style.display = 'flex';
    }
  }, []);

  // Helper function to generate stock symbol
  const getStockSymbol = (name: string) => {
    // Extract first letter of each word and convert to uppercase
    const symbol = name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 3) // Take first 3 letters max
    
    return symbol
  }

  const tickerStyle = {
    animation: 'ticker 60s linear infinite',
    WebkitAnimation: 'ticker 60s linear infinite',
    display: 'flex',
    opacity: 1,
    visibility: 'visible' as const,
  };

  const formatPrice = (price: string | number): string => {
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price
    return numericPrice.toFixed(2)
  }

  const handleCartClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsCartOpen(true)
  }

  return (
    <>
      <nav className={`w-full sticky-nav ${isShopPage ? 'bg-white border-b border-gray-200' : 'bg-black/95 border-b border-[#33ff33]/20'} relative z-50`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Link 
              href="/" 
              className={`px-4 py-1.5 border-r ${isShopPage ? 'border-gray-100' : 'border-[#1a1a1a]'} text-[10px] tracking-widest uppercase ${
                pathname === '/' 
                ? (isShopPage ? 'bg-gray-50 text-gray-900' : 'bg-[#1a1a1a] text-white')
                : (isShopPage ? 'text-gray-900 hover:bg-gray-50' : 'text-[#666] hover:text-white hover:bg-[#1a1a1a]')
              }`}
            >
              Home
            </Link>
            <Link 
              href="/shop" 
              className={`px-4 py-1.5 border-r ${isShopPage ? 'border-gray-100' : 'border-[#1a1a1a]'} text-[10px] tracking-widest uppercase ${
                pathname === '/shop' 
                ? (isShopPage ? 'bg-gray-50 text-gray-900' : 'bg-[#1a1a1a] text-white')
                : (isShopPage ? 'text-gray-900 hover:bg-gray-50' : 'text-[#33ff33] hover:text-[#33ff33] hover:bg-[#1a1a1a]')
              } flex items-center gap-2`}
            >
              Shop
              <div className="w-1.5 h-1.5 rounded-full bg-[#33ff33] shadow-[0_0_6px_#33ff33] animate-pulse"></div>
            </Link>
            <div 
              className={`px-4 py-1.5 border-r ${isShopPage ? 'border-gray-100' : 'border-[#1a1a1a]'} text-[10px] tracking-widest uppercase text-[#666] relative cursor-not-allowed flex items-center gap-2 group`}
              title={process.env.NODE_ENV === 'production' ? 'Access Restricted' : 'Development Mode Only'}
            >
              Intel
              <span className="text-xs">🔒</span>
              <div className="absolute top-8 left-0 w-48 bg-black/80 text-white text-[8px] p-2 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {process.env.NODE_ENV === 'production' 
                  ? 'Access to INTEL section is restricted.'
                  : 'INTEL section is available in development mode only.'}
              </div>
            </div>
          </div>
          
          {/* Audio and Cart Icons */}
          <div className="flex items-center gap-2">
            {/* Audio Controls */}
            <div className="flex items-center">
              {/* Volume Icon */}
              <button 
                onClick={toggleAudio}
                className={`px-3 py-1.5 flex items-center ${
                  isShopPage ? 'text-gray-600 hover:text-gray-900' : 'text-[#666] hover:text-white'
                } transition-colors relative`}
                title={isPlaying ? "Pause Music" : "Play Music"}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  className="w-4 h-4"
                >
                  {isPlaying ? (
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M15.536 8.464a5 5 0 010 7.072M17.95 6.05a8 8 0 010 11.9M6.5 8.688l4.469-4.469a1 1 0 011.823.557v14.448a1 1 0 01-1.823.557L6.5 15.312a1 1 0 00-.7-.283H4a1 1 0 01-1-1V9.971a1 1 0 011-1h1.8a1 1 0 00.7-.283z" 
                    />
                  ) : (
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" 
                    />
                  )}
                </svg>
                {isPlaying && (
                  <span className={`absolute -top-1 -right-1 ${
                    isShopPage ? 'bg-blue-600' : 'bg-[#33ff33]'
                  } w-2 h-2 rounded-full animate-pulse`}></span>
                )}
              </button>
              
              {/* Volume controls - only show when audio is playing */}
              {isPlaying && (
                <div className="flex items-center">
                  <button
                    onClick={() => adjustVolume('down')}
                    className={`px-1 py-1 text-xs ${
                      isShopPage ? 'text-gray-500 hover:text-gray-700' : 'text-[#555] hover:text-[#999]'
                    }`}
                    title="Decrease Volume"
                  >
                    -
                  </button>
                  <div className={`text-xs ${
                    isShopPage ? 'text-gray-500' : 'text-[#555]'
                  }`}>
                    {Math.round(volume * 100)}%
                  </div>
                  <button
                    onClick={() => adjustVolume('up')}
                    className={`px-1 py-1 text-xs ${
                      isShopPage ? 'text-gray-500 hover:text-gray-700' : 'text-[#555] hover:text-[#999]'
                    }`}
                    title="Increase Volume"
                  >
                    +
                  </button>
                </div>
              )}
            </div>
            
            {/* Cart Icon */}
            <button 
              onClick={handleCartClick}
              className={`px-4 py-1.5 flex items-center gap-1 ${
                isShopPage ? 'text-gray-600 hover:text-gray-900' : 'text-[#666] hover:text-white'
              } transition-colors relative`}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                className="w-4 h-4"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" 
                />
              </svg>
              {itemCount > 0 && (
                <span className={`absolute -top-1 -right-1 ${
                  isShopPage ? 'bg-blue-600' : 'bg-[#33ff33]'
                } text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full`}>
                  {itemCount}
                </span>
              )}
            </button>
            
            {itemCount > 0 && (
              <button
                onClick={() => {
                  clearCart();
                  setNotification('Cart cleared');
                  setTimeout(() => setNotification(null), 2000);
                }}
                className={`px-2 py-1 text-[10px] ${
                  isShopPage ? 'text-red-500 hover:text-red-600' : 'text-red-500 hover:text-red-400'
                } transition-colors uppercase tracking-wider`}
              >
                Clear Cart
              </button>
            )}
          </div>
          
          {notification && (
            <div className={`fixed top-4 right-4 ${
              isShopPage ? 'bg-blue-600' : 'bg-[#33ff33]/90'
            } text-white px-4 py-2 rounded z-50 text-sm font-mono`}>
              {notification}
            </div>
          )}
        </div>

        <div className="status-ticker-container">
          <div className="status-ticker-content" style={tickerStyle}>
            {products.map((product, index) => (
              <div key={index} className="status-item">
                <span className={`text-[10px] ${isShopPage ? 'text-gray-500' : 'text-gray-400'} flex items-center gap-2`}>
                  <span 
                    className="px-1.5 py-0.5 rounded-sm inline-flex items-center"
                    style={{
                      background: isShopPage 
                        ? 'linear-gradient(180deg, #ffffff 0%, #f5f5f5 50%, #eeeeee 51%, #f5f5f5 100%)'
                        : 'linear-gradient(180deg, #4a4a4a 0%, #3a3a3a 50%, #2a2a2a 51%, #3a3a3a 100%)',
                      boxShadow: isShopPage
                        ? 'inset 0 1px 0 0 rgba(255,255,255,0.15), 0 1px 2px 0 rgba(0,0,0,0.05)'
                        : 'inset 0 1px 0 0 rgba(255,255,255,0.15), 0 1px 2px 0 rgba(0,0,0,0.15)',
                      border: isShopPage ? '1px solid #e5e5e5' : '1px solid #1a1a1a'
                    }}
                  >
                    <span className={isShopPage ? "text-gray-400" : "text-[#888]"}>$</span>
                    <span className={isShopPage ? "text-gray-900" : "text-gray-200"}>{getStockSymbol(product.name)}</span>
                  </span>
                  {product.sku}
                </span>
                <span className={`text-[10px] ${isShopPage ? 'text-gray-900' : 'text-white'}`}>${formatPrice(product.price)}</span>
                <span className={`text-[10px] ${
                  product.change >= 0 
                    ? isShopPage ? 'text-green-600' : 'text-[#33ff33]'
                    : 'text-red-500'
                }`}>
                  {product.change >= 0 ? '▲' : '▼'} {Math.abs(product.change).toFixed(2)}%
                </span>
                <span className={`text-[10px] ${isShopPage ? 'text-gray-500' : 'text-gray-500'}`}>VOL: {product.volume}</span>
                <div className={`h-3 w-[1px] ${isShopPage ? 'bg-gray-200' : 'bg-[#333]'}`}></div>
              </div>
            ))}
            {/* Duplicate items for seamless loop */}
            {products.map((product, index) => (
              <div key={`dup-${index}`} className="status-item">
                <span className={`text-[10px] ${isShopPage ? 'text-gray-500' : 'text-gray-400'} flex items-center gap-2`}>
                  <span 
                    className="px-1.5 py-0.5 rounded-sm inline-flex items-center"
                    style={{
                      background: isShopPage 
                        ? 'linear-gradient(180deg, #ffffff 0%, #f5f5f5 50%, #eeeeee 51%, #f5f5f5 100%)'
                        : 'linear-gradient(180deg, #4a4a4a 0%, #3a3a3a 50%, #2a2a2a 51%, #3a3a3a 100%)',
                      boxShadow: isShopPage
                        ? 'inset 0 1px 0 0 rgba(255,255,255,0.15), 0 1px 2px 0 rgba(0,0,0,0.05)'
                        : 'inset 0 1px 0 0 rgba(255,255,255,0.15), 0 1px 2px 0 rgba(0,0,0,0.15)',
                      border: isShopPage ? '1px solid #e5e5e5' : '1px solid #1a1a1a'
                    }}
                  >
                    <span className={isShopPage ? "text-gray-400" : "text-[#888]"}>$</span>
                    <span className={isShopPage ? "text-gray-900" : "text-gray-200"}>{getStockSymbol(product.name)}</span>
                  </span>
                  {product.sku}
                </span>
                <span className={`text-[10px] ${isShopPage ? 'text-gray-900' : 'text-white'}`}>${formatPrice(product.price)}</span>
                <span className={`text-[10px] ${
                  product.change >= 0 
                    ? isShopPage ? 'text-green-600' : 'text-[#33ff33]'
                    : 'text-red-500'
                }`}>
                  {product.change >= 0 ? '▲' : '▼'} {Math.abs(product.change).toFixed(2)}%
                </span>
                <span className={`text-[10px] ${isShopPage ? 'text-gray-500' : 'text-gray-500'}`}>VOL: {product.volume}</span>
                <div className={`h-3 w-[1px] ${isShopPage ? 'bg-gray-200' : 'bg-[#333]'}`}></div>
              </div>
            ))}
          </div>
        </div>
      </nav>

      <CartDrawer 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)} 
      />
    </>
  )
} 